import { toNumber, toNumberTimestamp } from '../../../../util.js';
import type { ResolvedAxisChartSettings } from '../../../../components/GeneratedAxisChart/hooks/useGeneratedAxisChart.types';
import type { PathAxis, PathDataProps, PathRendererProps } from '../Path.types';

export type Point = [number, number];

type ResolvedAxis = ResolvedAxisChartSettings['axes'][keyof ResolvedAxisChartSettings['axes']];

export const getPathAxis = (axis: ResolvedAxis, key?: string): PathAxis | undefined =>
  axis?.scale && key ? { key, scale_type: axis.scale_type, scale: axis.scale, range: axis.range } : undefined;

const Y_SIDES = ['left', 'right'] as const;

export const getValueKeyAxes = ({ axes }: PathRendererProps): { key: string; axes: PathRendererProps['axes'] }[] =>
  Y_SIDES.flatMap((side) => {
    const axis = axes?.[side];
    if (!axis || axis.scale_type !== 'linear') return [];
    const other = side === 'left' ? 'right' : 'left';
    const keys = axis.value_keys?.length ? axis.value_keys : axis.value_key ? [axis.value_key] : [];
    return keys.map((key) => ({ key, axes: { ...axes, [other]: undefined, [side]: { ...axis, value_keys: undefined, value_key: key } } }));
  });

export const getPathAxes = ({ axes }: PathRendererProps): { x?: PathAxis; y?: PathAxis } => {
  const x_axis = axes?.bottom ?? axes?.top;
  const y_axis = axes?.left ?? axes?.right;
  return {
    x: getPathAxis(x_axis, x_axis?.value_key),
    y: getPathAxis(y_axis, y_axis?.value_key),
  };
};

const coordinate = (row: Record<string, unknown>, axis: PathAxis): number | undefined => {
  const raw = row[axis.key];
  const value = axis.scale_type === 'time' ? toNumberTimestamp(raw)
    : axis.scale_type === 'linear' ? toNumber(raw) : raw;
  return value === null || value === undefined ? undefined : axis.scale(value);
};

/** Preserve data order; missing or invalid coordinates start a new segment. */
export const getPathSegments = ({ data, x, y }: PathDataProps): Point[][] => {
  const segments: Point[][] = [];
  let segment: Point[] = [];
  for (const row of data) {
    const px = coordinate(row, x);
    const py = coordinate(row, y);
    if (px === undefined || py === undefined || !Number.isFinite(px) || !Number.isFinite(py)) {
      segment = [];
      continue;
    }
    if (!segment.length) segments.push(segment);
    segment.push([px, py]);
  }
  return segments;
};
